/* eslint-disable arrow-body-style */
import React from 'react';
import moment from 'moment';
import { YAXisComponentOption } from 'echarts';
import ColumnsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import { getSortedDates } from '@intelligence/utils/commonUtils';
import { formatDateStr, getElementStyleMathValue } from '@gp/utils/common';
import { FIXED_HEADER_HEIGHT, getProjectDetailsHeaderHeight } from '@gp/pages/Pipeline/ProjectDetails/utils';
import { CONSTANT_KEYS, CONSTANT_TEXTS } from '@gp/constants/const';
import { BASE_LINE_CHART_TOOLTIP_AXIS_POINTER_OPTION, BASE_ECHART_OPTION } from '@gp/constants/echarts';
import { FinancialsDataMode } from '../enums';
import { FINANCE_MODULE_KEY_MAPPING } from '../constants';

import type { TableColumn } from 'tea-component';

const TYPE_NAMES = {
  [CONSTANT_KEYS.ACTUAL]: CONSTANT_TEXTS.ACTUAL,
  [CONSTANT_KEYS.BUDGET]: CONSTANT_TEXTS.BUDGET,
  [CONSTANT_KEYS.FORECAST]: CONSTANT_TEXTS.FORECAST,
};
export const getTypeName = (type?: string): string => {
  if (!type) return '';
  return TYPE_NAMES[type] || '';
};
export const typeNameFormatter = (type: string) => getTypeName(type) || type || '-';

export const getLineXAxisData = (
  lineData: Record<string, any[]>,
  metric: string,
  financialsDataMode: FinancialsDataMode,
) => {
  if (!Reflect.has(lineData, metric)) return [];
  const linexAxisSetTmp: Set<string> = new Set();
  const listTmp = lineData?.[metric] || [];
  listTmp?.forEach((item: any) => {
    item?.history?.forEach((subItem: any) => {
      const historyTime = subItem?.time;
      if (historyTime) {
        linexAxisSetTmp.add(historyTime);
      }
    });
  });
  const linexAxisArrTmp = Array.from(linexAxisSetTmp);
  // 按时间排序升序排序
  return financialsDataMode === FinancialsDataMode.ByMilestone
    ? linexAxisArrTmp
    : getSortedDates(linexAxisArrTmp, 'asc');
};

const mergeYAxisOption = (stdOption?: YAXisComponentOption, option?: YAXisComponentOption): YAXisComponentOption => {
  const splitLineOption = option?.splitLine;
  const splitLineStyleOption = splitLineOption?.lineStyle;
  return {
    ...option,
    axisLabel: {
      ...option?.axisLabel,
      ...stdOption?.axisLabel,
    },
    splitLine: {
      ...splitLineOption,
      lineStyle: {
        ...splitLineStyleOption,
        ...stdOption?.splitLine?.lineStyle,
      },
    },
  } as any;
};

const mergeYAxisOptions = (stdOptions: any, options: any): any => {
  if (!options) return [];
  const stdOption = stdOptions?.[0];
  if (!Array.isArray(options)) return mergeYAxisOption(stdOption, options);
  return options.map((option) => mergeYAxisOption(stdOption, option));
};

export const mergeLineOptions = (lineOption: any): any => {
  return {
    ...BASE_ECHART_OPTION,
    grid: {
      ...lineOption?.grid,
    },
    legend: {
      ...BASE_ECHART_OPTION.legend,
      formatter: typeNameFormatter,
    },
    tooltip: {
      ...BASE_ECHART_OPTION.tooltip,
      axisPointer: {
        ...BASE_LINE_CHART_TOOLTIP_AXIS_POINTER_OPTION,
      },
      formatter: lineOption?.tooltip?.formatter,
    },
    xAxis: {
      ...BASE_ECHART_OPTION.xAxis,
      axisLine: {
        // @ts-ignore
        ...BASE_ECHART_OPTION.xAxis?.axisLine,
        onZero: !!lineOption?.xAxis?.axisLine?.onZero,
      },
      data: lineOption?.xAxis?.data,
    },
    yAxis: mergeYAxisOptions(BASE_ECHART_OPTION.yAxis, lineOption?.yAxis),
    series: lineOption?.series,
  };
};

export const getTableDynamicColumns = <T extends { date: string }>(
  financialsDataMode: FinancialsDataMode,
  list?: T[],
  cfg?: {
    widthByMonth?: number;
    widthByMilestone?: number;
    [key: string]: any;
  },
): TableColumn<T>[] => {
  if (!Array.isArray(list) || !list.length) return [] as TableColumn<T>[];
  const { widthByMilestone, widthByMonth, ...restCfg } = cfg || {};
  const isByMilestoneMode = financialsDataMode === FinancialsDataMode.ByMilestone;
  const width = isByMilestoneMode ? widthByMilestone : widthByMonth;
  return list.map((task) => {
    const { date } = task || {};
    const dateFormatted = isByMilestoneMode ? date : formatDateStr(date, 'YYYY-MM');
    return {
      ...restCfg,
      key: date,
      align: 'center' as TableColumn['align'],
      width: 140,
      header: (
        <ColumnsCenter width={width} align="right">
          <span>{dateFormatted}</span>
        </ColumnsCenter>
      ),
      render: (cvm: T) => {
        const dateVal = (cvm as any)?.[date] || '';
        return (
          <ColumnsCenter width={width} align="right">
            <span>{dateVal}</span>
          </ColumnsCenter>
        );
      },
    };
  });
};

export const getDateRangeByCategory = (key?: '1' | '2' | '3' | '4' | (string & {})) => {
  // For Last 6 Months
  const sixMonthsAgo = moment().subtract(5, 'months');
  const lastSixStart = moment(sixMonthsAgo).startOf('month').format('YYYY-MM-DD');

  // For Last 12 Months
  const twelveMonthsAgo = moment().subtract(11, 'months');
  const lastTwelveStart = moment(twelveMonthsAgo).startOf('month').format('YYYY-MM-DD');

  const mapKey: Record<string, { start: string; end: string }> = {
    '1': {
      start: '',
      end: '',
    },
    '2': {
      start: lastSixStart,
      end: moment().endOf('month').format('YYYY-MM-DD'),
    },
    '3': {
      start: lastTwelveStart,
      end: moment().endOf('month').format('YYYY-MM-DD'),
    },
  };
  return mapKey?.[key as any] || mapKey['1'];
};

export const scroll2Module = (rootNode?: HTMLElement | null, moduleKey?: string) => {
  if (!rootNode) return;
  switch (moduleKey) {
    // REMARK: Key Metrics模块暂时不需要滚动
    case FINANCE_MODULE_KEY_MAPPING.KEY_METRICS: {
      break;
    }
    case FINANCE_MODULE_KEY_MAPPING.TRENDS: {
      const headerHeight = getProjectDetailsHeaderHeight();
      const keyMetricsNode = rootNode?.querySelector('.dbg-finance-detail .dbg-finance-detail__key-metrics');
      const distance =
        headerHeight + getElementStyleMathValue(keyMetricsNode as Element, 'height') - FIXED_HEADER_HEIGHT;
      rootNode.scrollTo({ top: distance, behavior: 'smooth' });
      break;
    }
    default: {
    }
  }
};
